// STM language registration for Monaco: syntax highlighting plus hover,
// go-to-definition, and completion providers backed by the compiler's language
// service (../src/language-service). Everything works in plain source offsets;
// these are thin adapters mapping Monaco positions to the service and back.
//
// Highlighting is *not* a bespoke Monaco tokenizer. Instead the playground and
// the VS Code extension share a single source of truth: the TextMate grammar in
// editors/vscode/syntaxes/stm.tmLanguage.json. Here we feed that same grammar to
// Monaco through Shiki (vscode-textmate + Oniguruma, with the wasm inlined into
// the bundle), so both editors tokenize identically. See `setupHighlighting`.

import type * as MonacoNS from "monaco-editor";
import { createHighlighterCore } from "shiki/core";
import { createOnigurumaEngine } from "shiki/engine/oniguruma";
import onigWasm from "shiki/wasm";
import { shikiToMonaco } from "@shikijs/monaco";
import type { LanguageRegistration, ThemeRegistrationRaw } from "@shikijs/types";
import { analyzeSource, STDLIB_FILES } from "../src/compiler.js";
import type { DocumentSymbol, SignatureHelp } from "../src/compiler.js";
import type { Span } from "../src/frontend/token.js";
// The canonical grammar, shared verbatim with the VS Code extension.
import stmGrammar from "../editors/vscode/syntaxes/stm.tmLanguage.json";

type Monaco = typeof MonacoNS;

export const LANG_ID = "stm";
/** Monaco theme names defined once highlighting is ready (see `setupHighlighting`). */
export const THEME_DARK = "stm-dark";
export const THEME_LIGHT = "stm-light";

/** URI scheme + path for the model-per-file scheme the playground uses: the
 *  user's program lives at `stm:/program.stm`, each bundled stdlib file at
 *  `stm-stdlib:/<id>`. Definitions, references, and the file tabs all resolve to
 *  these same models, so navigation lands in one shared set of buffers. */
const STDLIB_SCHEME = "stm-stdlib";
const PROGRAM_PATH = "program.stm";

export function programUri(monaco: Monaco): MonacoNS.Uri {
  return monaco.Uri.parse(`stm:/${PROGRAM_PATH}`);
}

/** The file id a model URI names: `""` for the user's program, a stdlib file id
 *  (e.g. `"iter.stm"`) otherwise. The inverse of `uriForFile`. */
export function fileIdOf(uri: MonacoNS.Uri): string {
  return uri.scheme === STDLIB_SCHEME ? uri.path.replace(/^\//, "") : "";
}

/** The model URI for a file id (`""` = the program), creating the read-only
 *  stdlib model on first use so a go-to-definition target always has a buffer. */
export function uriForFile(monaco: Monaco, fileId: string): MonacoNS.Uri {
  return fileId ? stdlibModelUri(monaco, fileId) : programUri(monaco);
}

/** All position queries analyze the *program* (its index also covers the
 *  prelude), regardless of which file tab the cursor is in — a query in a stdlib
 *  tab still resolves against the one shared index. The parser recovers from
 *  syntax errors, so the index stays usable mid-edit. */
function programService(monaco: Monaco) {
  const model = monaco.editor.getModel(programUri(monaco));
  return analyzeSource(model ? model.getValue() : "");
}

// -- cross-file navigation bridge ------------------------------------------
// The definition/reference providers return targets in other files' models; the
// standalone editor can't open those on its own, so the Editor component
// registers a handler that switches the active file tab and reveals the target.

type NavigateHandler = (fileId: string, line: number, col: number) => void;
let navigateHandler: NavigateHandler | null = null;

/** Register (or clear) the handler that opens a file+position — used by the
 *  editor opener below to turn a cross-file jump into a tab switch. */
export function setNavigateHandler(fn: NavigateHandler | null): void {
  navigateHandler = fn;
}

export function spanToRange(span: Span): MonacoNS.IRange {
  return {
    startLineNumber: span.start.line,
    startColumn: span.start.col,
    endLineNumber: span.end.line,
    // Ensure a non-empty range so single-token jumps/hovers are selectable.
    endColumn: span.end.line === span.start.line ? Math.max(span.end.col, span.start.col + 1) : span.end.col,
  };
}

// Shiki/TextMate themes keyed by the same scopes the grammar emits. These mirror
// VS Code's Dark+ / Light+ so the playground looks like the extension under those
// themes: functions yellow, types teal, and two shades of blue — a lighter one
// for locals (`variable.other`) and a brighter one for struct members
// (`variable.other.property`).
const DARK_THEME: ThemeRegistrationRaw = {
  name: THEME_DARK,
  type: "dark",
  colors: {
    "editor.background": "#1e1e1e",
    "editor.foreground": "#d4d4d4",
  },
  settings: [
    { settings: { background: "#1e1e1e", foreground: "#d4d4d4" } },
    { scope: ["comment"], settings: { foreground: "#6a9955", fontStyle: "italic" } },
    { scope: ["string"], settings: { foreground: "#ce9178" } },
    { scope: ["constant.character.escape"], settings: { foreground: "#d7ba7d" } },
    { scope: ["constant.numeric"], settings: { foreground: "#b5cea8" } },
    { scope: ["keyword.control"], settings: { foreground: "#c586c0" } },
    { scope: ["keyword.declaration", "constant.language", "variable.language"], settings: { foreground: "#569cd6" } },
    { scope: ["keyword.operator"], settings: { foreground: "#d4d4d4" } },
    { scope: ["entity.name.type", "support.type"], settings: { foreground: "#4ec9b0" } },
    { scope: ["entity.name.function"], settings: { foreground: "#dcdcaa" } },
    { scope: ["variable.other"], settings: { foreground: "#9cdcfe" } },
    { scope: ["variable.other.property"], settings: { foreground: "#4fc1ff" } },
  ],
};

const LIGHT_THEME: ThemeRegistrationRaw = {
  name: THEME_LIGHT,
  type: "light",
  colors: {
    "editor.background": "#ffffff",
    "editor.foreground": "#000000",
  },
  settings: [
    { settings: { background: "#ffffff", foreground: "#000000" } },
    { scope: ["comment"], settings: { foreground: "#008000", fontStyle: "italic" } },
    { scope: ["string"], settings: { foreground: "#a31515" } },
    { scope: ["constant.character.escape"], settings: { foreground: "#ee0000" } },
    { scope: ["constant.numeric"], settings: { foreground: "#098658" } },
    { scope: ["keyword.control"], settings: { foreground: "#af00db" } },
    { scope: ["keyword.declaration", "constant.language", "variable.language"], settings: { foreground: "#0000ff" } },
    { scope: ["keyword.operator"], settings: { foreground: "#000000" } },
    { scope: ["entity.name.type", "support.type"], settings: { foreground: "#267f99" } },
    { scope: ["entity.name.function"], settings: { foreground: "#795e26" } },
    { scope: ["variable.other"], settings: { foreground: "#001080" } },
    { scope: ["variable.other.property"], settings: { foreground: "#0070c1" } },
  ],
};

/** Load the shared TextMate grammar through Shiki and wire it (plus the two STM
 *  themes) into Monaco. Async because it compiles the Oniguruma wasm; awaited by
 *  the editor so it can switch to an STM theme once tokenization is live. */
async function setupHighlighting(monaco: Monaco): Promise<void> {
  const highlighter = await createHighlighterCore({
    themes: [DARK_THEME, LIGHT_THEME],
    // Shiki keys languages by `name`; point it at LANG_ID so it lines up with
    // the Monaco language we registered (the grammar's own `scopeName` is kept).
    langs: [{ ...(stmGrammar as unknown as LanguageRegistration), name: LANG_ID }],
    engine: createOnigurumaEngine(onigWasm),
  });
  shikiToMonaco(highlighter, monaco);
}

function completionKind(monaco: Monaco, kind: string): MonacoNS.languages.CompletionItemKind {
  const K = monaco.languages.CompletionItemKind;
  switch (kind) {
    case "keyword":
      return K.Keyword;
    case "function":
      return K.Function;
    case "method":
      return K.Method;
    case "struct":
      return K.Struct;
    case "enum":
      return K.Enum;
    case "variant":
      return K.EnumMember;
    case "field":
      return K.Field;
    default:
      return K.Variable;
  }
}

function symbolKind(monaco: Monaco, kind: string): MonacoNS.languages.SymbolKind {
  const K = monaco.languages.SymbolKind;
  switch (kind) {
    case "struct":
      return K.Struct;
    case "field":
      return K.Field;
    case "enum":
      return K.Enum;
    case "variant":
      return K.EnumMember;
    case "function":
      return K.Function;
    case "method":
      return K.Method;
    case "trait":
      return K.Interface;
    case "impl":
      return K.Object;
    default:
      return K.Object;
  }
}

/** Map a language-service `SignatureHelp` to Monaco's shape. Each parameter is
 *  given as `[start, end)` offsets into the signature label so Monaco highlights
 *  the exact span (the tuple form of `ParameterInformation.label`). */
function toMonacoSignatureHelp(help: SignatureHelp): MonacoNS.languages.SignatureHelp {
  return {
    signatures: help.signatures.map((s) => ({
      label: s.label,
      parameters: s.params.map((p) => ({ label: p.range })),
    })),
    activeSignature: help.activeSignature,
    activeParameter: help.activeParam ?? 0,
  };
}

/** Map a language-service outline node (and its children) to a Monaco
 *  `DocumentSymbol` for the outline / breadcrumbs / symbol quick-pick. */
function toMonacoSymbol(monaco: Monaco, sym: DocumentSymbol): MonacoNS.languages.DocumentSymbol {
  return {
    name: sym.name,
    detail: sym.detail,
    kind: symbolKind(monaco, sym.kind),
    tags: [],
    range: spanToRange(sym.span),
    selectionRange: spanToRange(sym.selectionSpan),
    children: sym.children.map((c) => toMonacoSymbol(monaco, c)),
  };
}

/** If the cursor sits just after `ident(.ident)*.partial`, return the receiver
 *  path (so completion can offer that type's members). */
function receiverPathBefore(model: MonacoNS.editor.ITextModel, position: MonacoNS.Position): string[] | null {
  const line = model.getLineContent(position.lineNumber).slice(0, position.column - 1);
  const m = /([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*)\.[A-Za-z0-9_]*$/.exec(line);
  return m ? m[1]!.split(".") : null;
}

/** A stable virtual model URI for a bundled stdlib file, created on first use.
 *  The playground has no filesystem, so a prelude go-to-definition target is
 *  resolved to an in-memory Monaco model holding the file's source (with STM
 *  highlighting), rather than a real file as in the VS Code extension. */
function stdlibModelUri(monaco: Monaco, id: string): MonacoNS.Uri {
  const uri = monaco.Uri.parse(`stm-stdlib:/${id}`);
  if (!monaco.editor.getModel(uri)) {
    const file = STDLIB_FILES.find((f) => f.id === id);
    monaco.editor.createModel(file ? file.src : "", LANG_ID, uri);
  }
  return uri;
}

let registration: Promise<void> | null = null;

/** Register the STM language and providers, and wire up highlighting. Returns a
 *  promise that resolves once the STM themes are defined (highlighting is live).
 *  Idempotent: Monaco is a module-level singleton shared across editor instances,
 *  so this only runs the first time an editor mounts. */
export function registerLanguage(monaco: Monaco): Promise<void> {
  if (registration) return registration;

  monaco.languages.register({ id: LANG_ID });

  monaco.languages.setLanguageConfiguration(LANG_ID, {
    comments: { lineComment: "//" },
    brackets: [
      ["{", "}"],
      ["(", ")"],
      ["[", "]"],
    ],
    autoClosingPairs: [
      { open: "{", close: "}" },
      { open: "(", close: ")" },
      { open: "[", close: "]" },
      { open: '"', close: '"' },
    ],
  });

  monaco.languages.registerHoverProvider(LANG_ID, {
    provideHover(model, position) {
      const src = fileIdOf(model.uri) || undefined;
      const h = programService(monaco).hoverAt(model.getOffsetAt(position), src);
      if (!h) return null;
      return { range: spanToRange(h.span), contents: [{ value: h.markdown }] };
    },
  });

  monaco.languages.registerDefinitionProvider(LANG_ID, {
    provideDefinition(model, position) {
      const src = fileIdOf(model.uri) || undefined;
      const target = programService(monaco).definitionAt(model.getOffsetAt(position), src);
      if (!target) return null;
      // The target may live in another file (a bundled stdlib file, or back in the
      // program from a stdlib tab). The browser has no filesystem, so each file is
      // backed by a Monaco model under a stable URI; the editor opener below turns
      // the jump into a tab switch.
      return { uri: uriForFile(monaco, target.source ?? ""), range: spanToRange(target) };
    },
  });

  monaco.languages.registerDocumentSymbolProvider(LANG_ID, {
    provideDocumentSymbols(model) {
      // The outline is per-file (breadcrumbs, the ⌘/Ctrl+Shift+O quick-pick), so it
      // analyzes the shown model's own content — the program or an open stdlib tab.
      return analyzeSource(model.getValue())
        .documentSymbols()
        .map((s) => toMonacoSymbol(monaco, s));
    },
  });

  monaco.languages.registerReferenceProvider(LANG_ID, {
    provideReferences(model, position, context) {
      const src = fileIdOf(model.uri) || undefined;
      // Usages now span every file; each result carries the `source` of the file
      // it lives in, so it maps to that file's model.
      const spans = programService(monaco).referencesAt(
        model.getOffsetAt(position),
        context.includeDeclaration,
        src
      );
      return spans.map((span) => ({ uri: uriForFile(monaco, span.source ?? ""), range: spanToRange(span) }));
    },
  });

  // Turn a cross-file jump (go-to-definition / clicking a reference in another
  // file) into a file-tab switch, so the target opens in the main editor rather
  // than an inert peek. Same-file jumps are handled by the editor itself.
  monaco.editor.registerEditorOpener({
    openCodeEditor(_source, resource, selectionOrPosition) {
      if (resource.scheme !== STDLIB_SCHEME && resource.path !== `/${PROGRAM_PATH}`) return false;
      if (!navigateHandler) return false;
      let line = 1;
      let col = 1;
      if (selectionOrPosition) {
        if ("lineNumber" in selectionOrPosition) {
          line = selectionOrPosition.lineNumber;
          col = selectionOrPosition.column;
        } else {
          line = selectionOrPosition.startLineNumber;
          col = selectionOrPosition.startColumn;
        }
      }
      navigateHandler(fileIdOf(resource), line, col);
      return true;
    },
  });

  monaco.languages.registerCompletionItemProvider(LANG_ID, {
    triggerCharacters: ["."],
    provideCompletionItems(model, position) {
      const offset = model.getOffsetAt(position);
      const svc = programService(monaco);
      const path = receiverPathBefore(model, position);
      const comps = path ? svc.memberCompletions(path, offset) : svc.identifierCompletions(offset);
      const word = model.getWordUntilPosition(position);
      const range: MonacoNS.IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };
      const K = monaco.languages.CompletionItemInsertTextRule;
      return {
        suggestions: comps.map((c) => ({
          label: c.label,
          kind: completionKind(monaco, c.kind),
          detail: c.detail,
          // A callable is completed with its parentheses opened and the cursor
          // placed inside (`$0`), then signature help is popped up — so the moment
          // you accept `has`, the argument hint appears. A plain value inserts its
          // bare label.
          insertText: c.callable ? `${c.label}($0)` : c.label,
          insertTextRules: c.callable ? K.InsertAsSnippet : undefined,
          command: c.callable
            ? { id: "editor.action.triggerParameterHints", title: "Parameter Hints" }
            : undefined,
          range,
        })),
      };
    },
  });

  monaco.languages.registerSignatureHelpProvider(LANG_ID, {
    // `(` opens a call; `,` moves to the next argument. Both re-query so the
    // highlighted parameter follows the cursor as arguments are typed.
    signatureHelpTriggerCharacters: ["(", ","],
    signatureHelpRetriggerCharacters: [","],
    provideSignatureHelp(model, position) {
      const src = fileIdOf(model.uri) || undefined;
      const help = programService(monaco).signatureHelpAt(model.getOffsetAt(position), src);
      if (!help) return null;
      return { value: toMonacoSignatureHelp(help), dispose: () => {} };
    },
  });

  monaco.languages.registerInlayHintsProvider(LANG_ID, {
    provideInlayHints(model, range) {
      // Inferred effects (after a return type) and binding types (after a name).
      // Analyzes the shown model's own content, like the outline provider, so the
      // offsets index this buffer; Monaco queries per visible range, so filter to it.
      const K = monaco.languages.InlayHintKind;
      const hints = analyzeSource(model.getValue())
        .inlayHints()
        .map((h) => ({
          position: model.getPositionAt(h.offset),
          label: h.label,
          kind: h.kind === "type" ? K.Type : undefined,
          paddingLeft: h.paddingLeft,
          paddingRight: h.paddingRight,
        }))
        .filter((h) => monaco.Range.containsPosition(range, h.position));
      return { hints, dispose: () => {} };
    },
  });

  registration = setupHighlighting(monaco);
  return registration;
}
